/**
 * Debounces a function; courtesy of:
 * https://davidwalsh.name/javascript-debounce-function
 *
 * @param {!Function} func The function to debounce.
 * @param {number=} wait The amount of time to wait before executing func.
 * @param {boolean=} immediate Whether to execute func on the leading side of
 *    the debounce.
 * @return {!Function} A debounced version of the function.
 */
export function debounce(func, wait=100, immediate=false) {
  let timeout;
  return function() {
    const context = this;
    const args = arguments;
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
};


/**
 * Formats a milliseconds timestamp to YYYYMMDDHH.
 * @param {!Date} date A Date object.
 * @return {string} A string in the format of 'YYYYMMDDHH'.
 */
export function formatTime(date) {
  let month = String(date.getMonth() + 1);
  let day = String(date.getDate());
  let hours = date.getHours();

  // NOAA has data in intervals of 6 hours.
  hours = String((Math.round(hours / 6) * 6) || 0);

  if (month.length === 1) month = '0' + month;
  if (day.length === 1) day = '0' + day;
  if (hours.length === 1) hours = '0' + hours;

  return `${date.getFullYear()}${month}${day}${hours}`;
}


/**
 * Browser shim for getting the requestAnimationFrame function.
 */
export const getAnimationFrame = (function (){
  return window.requestAnimationFrame ||
    window.webkitRequestAnimationFrame ||
    window.mozRequestAnimationFrame ||
    window.oRequestAnimationFrame ||
    window.msRequestAnimationFrame ||
    function(callback) { window.setTimeout(callback, 1000 / 20); };
})();


/**
 * @param {*} val Any value.
 * @returns {boolean} Whether val is neither null nor undefined.
 */
export const isValue = function(val) {
  return val !== null && val !== undefined;
};

export const view = function() {
  const w = window;
  const d = document && document.documentElement;
  const b = document && document.getElementsByTagName("body")[0];
  const x = w.innerWidth || d.clientWidth || b.clientWidth;
  const y = w.innerHeight || d.clientHeight || b.clientHeight;
  return {width: x, height: y};
}

export const segmentedColorScale = function(segments) {
    const points = [], interpolators = [], ranges = [];
    for (let i = 0; i < segments.length - 1; i++) {
        points.push(segments[i+1][0]);
        interpolators.push(colorInterpolator(segments[i][1], segments[i+1][1]));
        ranges.push([segments[i][0], segments[i+1][0]]);
    }

    return function(point, alpha) {
        let i;
        for (i = 0; i < points.length - 1; i++) {
            if (point <= points[i]) {
                break;
            }
        }
        const range = ranges[i];
        return interpolators[i](proportion(point, range[0], range[1]), alpha);
    };
}

const colorInterpolator = function (start, end) {
    const r = start[0], g = start[1], b = start[2];
    const Δr = end[0] - r, Δg = end[1] - g, Δb = end[2] - b;
    return function(i, a) {
        return [Math.floor(r + i * Δr), Math.floor(g + i * Δg), Math.floor(b + i * Δb), a];
    };
}


export const proportion = function(x, low, high) {
    return (clamp(x, low, high) - low) / (high - low);
}

export const clamp = function(x, low, high) {
    return Math.max(low, Math.min(x, high));
}



// WEBPACK FOOTER //
// ./src/utilities/functions.js